// Registry of interactive guided tours. Each tour has a route to start on
// and an ordered list of steps. A step targets a DOM element via CSS
// selector; optional onEnter runs before the element is located (e.g. to
// open a dialog by clicking the real button — keeps the tour honest with
// the actual UI flow).

const ADD_YACHT_BTN = '[data-testid="add-yacht-btn"], [data-testid="empty-state-add-yacht"]';

export const TOURS = {
  "add-yacht": {
    route: "/app/yachts",
    steps: [
      {
        target: ADD_YACHT_BTN,
        title: "Add a yacht",
        body:
          "Everything in Seavanto — inventory, tasks, logbook, petty cash — is organised per yacht. This button opens the new-yacht form. The tour will open it for you on the next step.",
      },
      {
        target: '[data-tour="yacht-role"]',
        onEnter: () => {
          // Open the create dialog exactly the way a user would.
          if (!document.querySelector('[data-testid="yacht-form-dialog"]')) {
            document.querySelector(ADD_YACHT_BTN)?.click();
          }
        },
        title: "Pick your role",
        body:
          "Roles are stored per yacht — you can be the Owner of one yacht and the Captain of another. Your role decides what you can approve and delete on this yacht.",
      },
      {
        target: '[data-tour="yacht-cover"]',
        title: "Cover photo",
        body:
          "Optional, but nice: upload a photo of the yacht. It shows on the yacht card and makes switching between yachts easier at a glance.",
      },
      {
        target: '[data-tour="yacht-basics"]',
        title: "Basic details",
        body:
          "Only the yacht name is required. Brand, model, year, length, flag, registration and home marina are all optional and can be filled in later via Edit.",
      },
      {
        target: '[data-tour="yacht-engines"]',
        title: "Engines",
        body:
          'Click "Add engine" for each engine on board. Give it a Name (e.g. Port engine), pick the brand and type — power auto-fills from our catalog. Engine hours feed the logbook and periodic maintenance schedules.',
      },
      {
        target: '[data-tour="yacht-generators"]',
        title: "Generators, tenders & stabilizers",
        body:
          "Same idea for generators. Below them you can add tenders (each with its own engines) and stabilizers. All of it is optional and editable any time.",
      },
      {
        target: '[data-testid="save-yacht"]',
        title: "Create the yacht",
        body:
          'When you\'re happy, click "Create yacht". It becomes your active yacht right away, so every module is ready to use. We\'ll leave the form open so you can fill it in now.',
      },
    ],
  },
  "invite-crew": {
    route: "/app/crew",
    steps: [
      {
        target: '[data-testid="invite-btn"]',
        title: "Add crew",
        body:
          "Crew is managed per yacht. This button opens the add-crew dialog for your active yacht — the tour will open it for you on the next step. (Only owners, managers and captains can invite.)",
      },
      {
        target: '[data-tour="crew-invite-tabs"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="invite-email"]')) {
            document.querySelector('[data-testid="invite-btn"]')?.click();
          }
        },
        title: "Two ways to add someone",
        body:
          '"Invite new" emails a sign-up link to someone who isn\'t on Seavanto yet. "Add existing" lets you pick a person from your network who already has an account — they just get a notification.',
      },
      {
        target: '[data-testid="invite-email"]',
        title: "Their email",
        body: "Type the crew member's email address. The invitation link they receive lets them set a password and land directly on this yacht.",
      },
      {
        target: '[data-testid="invite-role"]',
        title: "Pick their role",
        body:
          "Roles follow the chain of command: you can only assign roles below your own (with exceptions, e.g. adding an owner when the yacht has none). The role decides what they can see and approve.",
      },
      {
        target: '[data-testid="send-invite"]',
        title: "Send the invite",
        body:
          'Click "Send invite" and they\'ll get the email right away. You can track pending invitations in the crew list below — and cancel them any time.',
      },
    ],
  },
  "logbook-entry": {
    route: "/app/logbook",
    steps: [
      {
        target: '[data-testid="add-log-btn"]',
        title: "Record a logbook entry",
        body:
          "The logbook keeps a tamper-evident record of trips, conditions and engine hours for your active yacht. This button opens a new entry — the tour will open it for you on the next step.",
      },
      {
        target: '[data-testid="log-type"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="log-date"]')) {
            document.querySelector('[data-testid="add-log-btn"]')?.click();
          }
        },
        title: "Log type",
        body:
          'Choose what kind of entry this is. "Trip" entries unlock extra fields: departure and arrival times, distance and average speed.',
      },
      {
        target: '[data-tour="log-fields"]',
        title: "Conditions & details",
        body:
          "Fill in the captain on duty, date, location, weather, sea state and fuel level. Only what you fill in gets recorded — everything is editable later, with full change history.",
      },
      {
        target: '[data-testid="engine-readings"]',
        optional: true,
        title: "Engine hours",
        body:
          "Enter the current hour counters for engines, generators, stabilizers and tenders. These readings drive your periodic maintenance schedules. Leave a field blank to skip it — values lower than the last reading are ignored.",
      },
      {
        target: '[data-testid="save-log"]',
        title: "Save the entry",
        body:
          'Click "Save entry" and it appears in the log list, stamped with your name. We\'ll leave the form open so you can record one now.',
      },
    ],
  },
  "my-network": {
    route: "/app/crew?view=network",
    steps: [
      {
        target: '[data-testid="network-search"]',
        title: "Your personal network",
        body:
          "Everyone you've worked with across all your yachts lives here — crew and suppliers. Search by name, email or phone.",
      },
      {
        target: '[data-testid="network-role-filter"]',
        title: "Filter by role",
        body: "Narrow the crew list down to captains, engineers, deckhands… handy once your network grows.",
      },
      {
        target: '[data-testid="network-list"]',
        title: "Crew you've worked with",
        body:
          "People stay in your network even if the yacht you shared is deleted. Open a person's Info panel for contact and business details, or assign them straight onto one of your yachts.",
      },
      {
        target: '[data-testid="section-suppliers"]',
        title: "Suppliers",
        body:
          "Your service-provider address book — registered Seavanto providers and offline contacts you keep as records. Info shows bank details, attachments and assigned yachts.",
      },
      {
        target: '[data-testid="add-supplier-btn"]',
        title: "Add a supplier",
        body:
          "Add an offline supplier as a simple record — you can send them a Seavanto invite later from their Info panel. The tour opens the form on the next step.",
      },
      {
        target: '[data-testid="supplier-name"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="supplier-modal"]')) {
            document.querySelector('[data-testid="add-supplier-btn"]')?.click();
          }
        },
        title: "Supplier details",
        body:
          "Only the name is required. Contact person, email, phone, category and notes help your crew pick the right supplier on tasks and invoices. Business details (VAT, IBAN…) can pre-fill invoices.",
      },
      {
        target: '[data-testid="supplier-save"]',
        title: "Save",
        body: "Save the supplier and they appear in the list below — and become selectable everywhere suppliers are used.",
      },
    ],
  },
  "add-inventory": {
    route: "/app/inventory",
    steps: [
      {
        target: '[data-testid="scan-inv-btn"]',
        title: "Inventory, two ways",
        body:
          "Track spares and consumables per yacht. Fastest way in: Scan a product barcode — Seavanto looks it up and pre-fills the item for you.",
      },
      {
        target: '[data-testid="add-inv-btn"]',
        title: "Add items manually",
        body: "Or add an item by hand with the + button. The tour opens the form for you on the next step.",
      },
      {
        target: '[data-tour="inv-fields"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="inv-name"]')) {
            document.querySelector('[data-testid="add-inv-btn"]')?.click();
          }
        },
        title: "Item details",
        body:
          "Only the name is required. Category, location, supplier, price, expiry date and a photo make items easier to find and reorder.",
      },
      {
        target: '[data-testid="inv-min"]',
        title: "Min stock = low-stock alerts",
        body: "Set a minimum stock level and Seavanto flags the item on the dashboard when the quantity drops below it.",
      },
      {
        target: '[data-testid="save-inv"]',
        title: "Save it",
        body:
          'After saving, record every use, breakage or restock via the "Adjust" action on the row — that keeps a full audit trail instead of overwriting the count.',
      },
    ],
  },
  "create-task": {
    route: "/app/tasks",
    steps: [
      {
        target: '[data-testid="add-task-btn"]',
        title: "Create a task",
        body:
          "Tasks track one-off jobs — repairs, services, deliveries. The tour opens the form for you on the next step.",
      },
      {
        target: '[data-testid="maint-title"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="maint-title"]')) {
            document.querySelector('[data-testid="add-task-btn"]')?.click();
          }
        },
        title: "Title",
        body: "Give the task a clear title — it's the only required field.",
      },
      {
        target: '[data-tour="task-fields"]',
        title: "Classify & schedule",
        body:
          "Pick the system, priority and status, set a due date and cost estimate, describe the work and attach before-photos if useful.",
      },
      {
        target: '[data-testid="maint-assignee-toggle"]',
        title: "Assign it",
        body:
          "Assign to a crew member — they get notified and see it in their pending list — or to an external supplier from your network.",
      },
      {
        target: '[data-testid="save-maint"]',
        title: "Create the task",
        body:
          "Work flows open → in progress → completed, then a senior confirms to sign it off. We'll leave the form open so you can create one now.",
      },
    ],
  },
  "periodic-task": {
    route: "/app/maintenance",
    steps: [
      {
        target: '[data-testid="seed-routines-btn"]',
        title: "Start with templates",
        body:
          'New to periodic maintenance? Click "Seed templates" to drop ~17 common yacht tasks (engine oil change, generator service, life-raft check, anode inspection…) onto this yacht. It is the fastest way to get an idea of what to track — open any one to set its engine, interval and assignee, or just use them as inspiration.',
      },
      {
        target: '[data-testid="new-routine-btn"]',
        title: "Or add your own",
        body:
          "Prefer to build from scratch? Recurring maintenance — oil changes, generator services. When one falls due, Seavanto auto-creates a Job in the Tasks triggered section below. The tour opens the form on the next step.",
      },
      {
        target: '[data-testid="routine-title"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="routine-editor"]')) {
            document.querySelector('[data-testid="new-routine-btn"]')?.click();
          }
        },
        title: "Title & system",
        body: "Name the task and pick which system it belongs to — both are required.",
      },
      {
        target: '[data-testid="routine-cycle-kind"]',
        title: "Cycle basis",
        body:
          "Repeat every N calendar days, or every N engine hours — hour cycles are driven by your logbook readings. Some tasks track both (hours OR days, whichever comes first). Days-based tasks can also pin a first due date.",
      },
      {
        target: '[data-testid="routine-target-kind"]',
        optional: true,
        title: "Pin to an engine",
        body: "For engine systems, target a specific unit or group so the right hour counter drives the schedule.",
      },
      {
        target: '[data-testid="routine-assignee-toggle"]',
        title: "Assign it",
        body: "Choose who handles the job each time it comes due — a crew member or an external supplier.",
      },
      {
        target: '[data-testid="save-routine"]',
        title: "Create",
        body:
          "Save it and Seavanto takes over the scheduling — the next due date (or hour threshold) is shown in the list. We'll leave the form open for you.",
      },
    ],
  },
  checklist: {
    route: "/app/checklists",
    steps: [
      {
        target: '[data-testid="seed-defaults-btn"]',
        title: "Start with the defaults",
        body:
          'Best first step: click "Seed defaults" to load a base set of standard checklists (departure, arrival, safety…) onto this yacht. Use them as-is, or edit them to fit your boat.',
      },
      {
        target: '[data-testid="new-checklist-btn"]',
        title: "Or build your own",
        body: "Create a checklist from scratch. The tour opens the editor for you on the next step.",
      },
      {
        target: '[data-testid="tpl-name"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="template-editor"]')) {
            document.querySelector('[data-testid="new-checklist-btn"]')?.click();
          }
        },
        title: "Name it",
        body: 'Something the crew recognises instantly — e.g. "Pre-departure" or "Guest cabin turnaround".',
      },
      {
        target: '[data-testid="tpl-type-frequency"]',
        title: "Type & frequency",
        body: "Classify the checklist (departure, arrival, safety…) and how often it should run — this groups it in the right place.",
      },
      {
        target: '[data-testid="new-item-input"]',
        title: "Add the items",
        body: "Type each check and press Enter (or Add). You can rename or remove items any time — a checklist needs at least one.",
      },
      {
        target: '[data-testid="save-tpl"]',
        title: "Create & run",
        body:
          "Save the template, then open it from the grid and hit Run — that starts a live run your crew ticks off together, with full history kept.",
      },
    ],
  },
  "petty-cash": {
    route: "/app/petty-cash",
    steps: [
      {
        target: '[data-testid="pc-custodian-banner"]',
        title: "Who holds the cash?",
        body:
          "The cash custodian has custody of the physical float — they confirm receipt of top-ups and sign off expenses. Appoint one first (owners, managers and captains can, via Change/Appoint) so approvals route to the right person.",
      },
      {
        target: '[data-tour="pc-tiles"]',
        title: "The float at a glance",
        body:
          "Current balance counts only finalised entries — pending top-ups and expenses sit in the Pending tile until they're signed off.",
      },
      {
        target: '[data-testid="add-pc-btn"]',
        title: "Record cash movements",
        body: "Every top-up into the float and every expense out of it is logged here. The tour opens the form on the next step.",
      },
      {
        target: '[data-testid="pc-type"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="pc-amount"]')) {
            document.querySelector('[data-testid="add-pc-btn"]')?.click();
          }
        },
        title: "Top-up or expense",
        body:
          "Top-ups stay pending until the custodian confirms the cash was received. Expenses go up the approval chain to a senior before they hit the balance.",
      },
      {
        target: '[data-testid="pc-receipt"]',
        title: "Attach the receipt",
        body: "Snap a photo or attach a PDF of the receipt — it travels with the entry and keeps the accounting audit-proof.",
      },
      {
        target: '[data-testid="save-pc"]',
        title: "Submit",
        body:
          "The entry lands as pending; the custodian and approvers see a red badge in the sidebar until they act on it. We'll leave the form open for you.",
      },
    ],
  },
  "invoices": {
    route: "/app/invoices",
    steps: [
      {
        target: '[data-testid="inv-outstanding"]',
        optional: true,
        title: "What you owe at a glance",
        body:
          "These cards total what's been invoiced, what's still outstanding and how many invoices are overdue — so you always know the yacht's payment position.",
      },
      {
        target: '[data-testid="add-invoice-btn"]',
        title: "Log an invoice",
        body:
          "Record every supplier invoice here — yard bills, provisioning, parts. The tour opens the form on the next step.",
      },
      {
        target: '[data-testid="inv-supplier-picker"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="inv-amount"]')) {
            document.querySelector('[data-testid="add-invoice-btn"]')?.click();
          }
        },
        title: "Pick the supplier",
        body:
          "Choose an existing supplier or add a new one on the fly — invoices stay grouped by supplier so you can see who you spend the most with.",
      },
      {
        target: '[data-testid="inv-amount"]',
        title: "Amount, number & status",
        body:
          "Enter the amount and invoice number, then set the status — Waiting, Paid or Disputed. The outstanding and overdue totals update automatically.",
      },
      {
        target: '[data-testid="inv-bank-block"]',
        title: "Bank details for payment",
        body:
          "Capture the supplier's IBAN / account details alongside the invoice so whoever pays it has everything in one place. Prefill pulls a saved supplier's bank info.",
      },
      {
        target: '[data-testid="save-invoice"]',
        title: "Save the invoice",
        body:
          "Add it and it joins the list below, feeding the outstanding and overdue figures. We'll leave the form open for you.",
      },
    ],
  },
  "documents": {
    route: "/app/documents",
    steps: [
      {
        target: '[data-testid="add-doc-btn"]',
        title: "Upload a document",
        body:
          "Keep the yacht's paperwork in one place — registration, insurance, class certificates, manuals. The tour opens the upload form next.",
      },
      {
        target: '[data-testid="doc-name"]',
        onEnter: () => {
          if (!document.querySelector('[data-testid="doc-cat"]')) {
            document.querySelector('[data-testid="add-doc-btn"]')?.click();
          }
        },
        title: "Name it",
        body:
          "Give the document a clear name so the crew can find it later — e.g. 'Hull insurance 2026' or 'MTU engine manual'.",
      },
      {
        target: '[data-testid="doc-cat"]',
        title: "Categorise & set expiry",
        body:
          "Pick a category (the options follow your role's access) and add an expiry date for time-sensitive papers like certificates — you'll be warned before they lapse.",
      },
      {
        target: '[data-testid="doc-file"]',
        title: "Attach the file",
        body:
          "Upload the PDF, image or office file. Large files upload in chunks, so even big manuals go through reliably.",
      },
      {
        target: '[data-testid="save-doc"]',
        title: "Save it",
        body:
          "Save and the document appears in the table below, ready to open or download any time. We'll leave the form open for you.",
      },
    ],
  },
  "media": {
    route: "/app/photos",
    steps: [
      {
        target: '[data-testid="photo-upload"]',
        title: "Upload photos & videos",
        body:
          "Build a visual record of the yacht — condition shots, before/after of repairs, walkthroughs. You can upload several files at once, including video.",
      },
      {
        target: '[data-testid="photo-caption"]',
        title: "Add a caption",
        body:
          "Describe what the shot shows so it still makes sense months later — e.g. 'Port engine raw-water pump before service'.",
      },
      {
        target: '[data-testid="photo-tags"]',
        title: "Tag for easy finding",
        body:
          "Add comma-separated tags like 'engine room, provisioning, exterior'. Tags become quick filters above the gallery.",
      },
      {
        target: '[data-testid="photo-tag-all"]',
        optional: true,
        title: "Filter by tag",
        body:
          "Each tag shows how many items carry it — tap one to drill into just those photos and videos.",
      },
      {
        target: '[data-testid="photo-grid"]',
        title: "Your gallery",
        body:
          "Everything you upload lands here. Click any item to view it full-size, edit its caption and tags, or remove it.",
      },
    ],
  },

};
